// Core
import React, { useState, useEffect, useRef } from 'react';
import { 
	Box,
	Button,
	Flex,
	Text,
	Grid,
	FormControl,
	Input,
	InputGroup,
	InputLeftElement,
	useColorModeValue
} from '@chakra-ui/react';
import { useLocation, useHistory } from 'react-router-dom';

// Components
import Partners from '../Partners';
import { SearchIcon } from 'components/Icons/Icons';
import Card from 'components/Card/Card';
import Categories from './components/Categories';
import Discount from './components/Discounts';
import Stores from './components/Stores';
import Sort from './components/Sort';
import Explore from './components/Explore';
import Tags from './components/Tags';
import States from './components/States';
import MapWithPoints from './components/maps/Multy';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import { MapsIcon } from 'components/Icons/Icons';

const Partner = ({ userData }) => {
	const resetButtonColor = useColorModeValue('#262D47', '#9FABCB');
	const debounceRef = useRef(null);
	const inputSearchRef = useRef();

	const [partnersIsHidden, setPartnersIsHidden] = useState(false); 
	const [isLoadingPage, setIsLoadingPage] = useState(false); 
	const [search, setSearch] = useState('');
	const [categoryId, setCategoryId] = useState(null);
	const [tagIds, setTagIds] = useState(null);
	const [states, setStates] = useState(null);
	const [isPopular, setIsPopular] = useState(null);
	const [isFeatured, setIsFeatured] = useState(null);
	const [isShop, setIsShop] = useState(null);
	const [nearMe, setNearMe] = useState(null);
	const [selectedTags, setSelectedTags] = useState([]);
	const [discountsRange, setDiscountsRange] = useState([0,100]);
	const [selectedStates, setSelectedStates] = useState([]);

	const [filterResults, setFilterResults] = useState(0);

	const [isReady, setIsReady] = useState(false);

	const [mapView, setMapView] = useState(false);

	const location = useLocation();
	useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryFromUrl = searchParams.get('category');
        const tagsFromUrl = searchParams.get('tags');
        const statesFromUrl = searchParams.get('locations');
        const popularFromUrl = searchParams.get('sort');
        const featuredFromUrl = searchParams.get('tops');
        const shopFromUrl = searchParams.get('shop');
        const mapUrl = searchParams.get('map');
        const nearMeUrl = searchParams.get('nearMe');
        const discountUrl = searchParams.get('discount') ? decodeURIComponent(searchParams.get('discount')).split(',').map(Number) : [0,100];

        if (categoryFromUrl) { setCategoryId(categoryFromUrl); }
        if (tagsFromUrl) { setTagIds(tagsFromUrl); } else { setTagIds(null); }
        if (statesFromUrl) { setStates(statesFromUrl); } else { setStates(null); }
        if (popularFromUrl) { setIsPopular(popularFromUrl); }
        if (featuredFromUrl) { setIsFeatured(featuredFromUrl); }
        if (shopFromUrl) { setIsShop('shop'); }
        if (nearMeUrl) { setNearMe(true); }
        if (mapUrl) { setMapView(true); }
        if (discountUrl) { setDiscountsRange(discountUrl); }

        setIsReady(true);
    }, [location.search]);

	const handleSearch = (event) => {
	    setIsLoadingPage(true);

	    const value = event.target.value;
	    clearTimeout(debounceRef.current);
	    debounceRef.current = setTimeout(() => {
	      setSearch(value);
	    }, 600);
	};

	const history = useHistory();
	const handleMapView = () => {
		const searchParams = new URLSearchParams(window.location.search);
        setMapView(!mapView); 
        if (!mapView) {
            searchParams.set('map', 'true');
        } else {
            searchParams.delete('map');
        }

        history.push({ search: searchParams.toString() }); 			      
	};

	const handleResetFilters = () => {
		setSearch('');
		if (inputSearchRef.current) {
	      inputSearchRef.current.value = '';
	    }
		setCategoryId(null);
		setTagIds(null);
		setStates(null);
		setIsPopular(null);
		setIsFeatured(null);
		setIsShop(null);
		setNearMe(null);
		setSelectedTags([]);
		setMapView(false);
		history.push({ search: '' });
	};

	const [userCoords, setUserCoords] = useState({ lat: 0, long: 0 });
	useEffect(() => {
	    navigator.geolocation.getCurrentPosition(
	      (pos) => {
	        setUserCoords({
	          lat: pos.coords.latitude,
	          long: pos.coords.longitude
	        });
	      },
	      () => {
	        setUserCoords({ lat: 0, long: 0 });
	      }
	    );
	}, []);

	return (
		<Box pt="18px">

		<Breadcrumbs target="Discounts" url="/discounts" current="Discover" />

	    <Grid mt={{base: '16px', md: '32px'}} templateColumns={{ sm: '1fr', md: '368px 1fr' }} gap="24px" templateRows='1fr' mb="100px">
	    	<Box>
	    		<Flex justifyContent="space-between" alignItems="center">
	    			<Flex gap="12px" alignItems="center">
	    				<Text 
	    					variant="header"
	    					textTransform="capitalize" 
	    					fontStyle="normal"
	    					fontSize="24px" 
	    					fontWeight="800 !important">Filters</Text>
	    				<Text color={resetButtonColor}>({filterResults} Result)</Text>
	    			</Flex>

	    			<Button
	    				onClick={handleResetFilters}
	    				variant="secondary-md" h="48px" w="97px" color={resetButtonColor} fontWeight="500" borderRadius="full">
		        		Reset
		        	</Button>
	    		</Flex>

	    		<Card padding="12px" mt="24px">
	    			<Categories selectedCategory={categoryId} setCategoryId={setCategoryId} />
	    			{ userData?.activePlan && <Discount discountsRange={discountsRange} setDiscountsRange={setDiscountsRange}  /> }
	    			{ /* <Tags setSelectedTags={setSelectedTags} selectedTags={selectedTags}  /> */ }
	    			<States setSelectedStates={setSelectedStates} selectedStates={selectedStates}  />
	    			<Stores userData={userData} />
	    			<Explore 
	    				isPopular={isPopular} 
	    				setIsPopular={setIsPopular} 
	    				isFeatured={isFeatured} 
	    				setIsFeatured={setIsFeatured}
	    				isShop={isShop}
	    				setIsShop={setIsShop}
	    				nearMe={nearMe}
	    				setNearMe={setNearMe}
	    				mapView={mapView}
	    				/>
	    		</Card>
	    	</Box>

	    	<Box>
		    	<Flex gap="16px" mb="24px" alignItems="center">
			    	<FormControl id="search">
			          <InputGroup>
			            <InputLeftElement>
			              <SearchIcon fontSize="24px" color="#9FABCB" m="6px 0 0 6px" />
			            </InputLeftElement>
			            <Input
			              p="15px 45px!important"
			              h="48px !important"
			              onChange={handleSearch}
			              ref={inputSearchRef}
			              placeholder='Search'
			              autoComplete="off"
			            />
			          </InputGroup>
			        </FormControl>

			        <Sort 
			        	isPopular={isPopular} 
	    				setIsPopular={setIsPopular} 
	    				setIsLoadingPage={setIsLoadingPage}
			        	mapView={mapView} />

			        <Button 
			        	variant={ mapView ? 'button-md' : 'secondary-md' } 
			        	w="48px" 
			        	onClick={handleMapView}>
			        	<MapsIcon fontSize="24px" color={ mapView ? 'white' : '#9FABCB' }  />
			        </Button>
		        </Flex>

		        { !mapView ?
			        <Partners 
			        	key={`${search}_${categoryId}_${tagIds}_${isPopular}_${isFeatured}`} 
			        	userData={userData}
			        	type="partner" 
			        	search={search} 
			        	categoryId={categoryId}
			        	tagIds={tagIds}
			        	locations={states}
			        	discountsRange={discountsRange}
			        	isPopular={isPopular}
			        	isFeatured={isFeatured}
			        	isShop={isShop}
			        	isReady={isReady}
			        	nearMe={nearMe}
			        	limit={24} 
			        	userCoords={userCoords}
			        	isLoadingPage={isLoadingPage}
			        	setIsLoadingPage={setIsLoadingPage}
			        	setFilterResults={setFilterResults}
			        	setPartnersIsHidden={setPartnersIsHidden}
			        	includeLoadMore={true}/>
			        : 
			        <MapWithPoints  
			        	key={`${search}_${categoryId}_${tagIds}_${isPopular}_${isFeatured}`} 
			        	userData={userData}
			        	search={search} 
			        	categoryId={categoryId}
			        	discountsRange={discountsRange}
			        	tagIds={tagIds}
			        	locations={states}
			        	isPopular={isPopular}
			        	isFeatured={isFeatured}
			        	isShop={isShop}
			        	nearMe={nearMe}
			        	userCoords={userCoords}
			        	isReady={isReady}
			        	setFilterResults={setFilterResults}
			        	/> 
			    }
	        </Box>
	        
	    </Grid>

	    </Box>
	);
};

export default Partner;